import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_POLICY, Policy } from '../constants/policy';
import { configAPI } from '../services/api';

interface PolicyContextValue extends Policy {
  /** False until the server has answered (or failed). Defaults apply meanwhile. */
  loaded: boolean;
}

const PolicyContext = createContext<PolicyContextValue | undefined>(undefined);

/**
 * Loads the live policy (cancellation window, mistake window, rollout
 * switches) from the API once, so every environment shows the numbers its
 * server enforces. Falls back to the compiled defaults if the call fails.
 */
export const PolicyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [policy, setPolicy] = useState<Policy>(DEFAULT_POLICY);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await configAPI.getPolicy();
        if (!cancelled && p) {
          setPolicy({
            windowHours: Number(p.cancellation_window_hours ?? DEFAULT_POLICY.windowHours),
            graceMinutes: Number(p.booking_grace_minutes ?? DEFAULT_POLICY.graceMinutes),
            selfCancelEnabled: p.self_cancel_enabled ?? DEFAULT_POLICY.selfCancelEnabled,
            guidesEnabled: p.guides_enabled ?? DEFAULT_POLICY.guidesEnabled,
          });
        }
      } catch {
        // keep defaults
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => ({ ...policy, loaded }), [policy, loaded]);
  return <PolicyContext.Provider value={value}>{children}</PolicyContext.Provider>;
};

/** Live policy; safe to call outside the provider (returns the defaults). */
export const usePolicy = (): PolicyContextValue => {
  const ctx = useContext(PolicyContext);
  return ctx ?? { ...DEFAULT_POLICY, loaded: false };
};
